import { useCallback, useState } from 'react';
import type { ComparisonAlignment, ComparisonMetric } from '../../../utils/comparison/alignStreams';
import {
  defaultSession,
  sessionFromPrefill,
  type ComparisonMode,
  type ComparisonPrefill,
  type ComparisonSession,
  type ComparisonViewMode,
  type SegmentRange,
  type WorkoutSummary,
} from '../types';

export function useComparisonSession(initialPrefill: ComparisonPrefill | null) {
  const [session, setSession] = useState<ComparisonSession>(() => sessionFromPrefill(initialPrefill));
  const [compareTick, setCompareTick] = useState(0);

  const setMode = useCallback((mode: ComparisonMode) => {
    setSession(s => ({
      ...s,
      mode,
      viewMode: mode === 'one-workout-segments' ? 'segments' : s.viewMode,
      workoutB: mode === 'one-workout-segments' ? s.workoutA : s.workoutB,
    }));
  }, []);

  const setViewMode = useCallback((viewMode: ComparisonViewMode) => {
    setSession(s => ({ ...s, viewMode }));
  }, []);

  const setMetric = useCallback((metric: ComparisonMetric) => {
    setSession(s => ({ ...s, metric }));
  }, []);

  const setAlignment = useCallback((alignment: ComparisonAlignment) => {
    setSession(s => ({ ...s, alignment }));
  }, []);

  const setWorkoutA = useCallback((workout: WorkoutSummary | null) => {
    setSession(s => ({
      ...s,
      workoutA: workout,
      workoutB: s.mode === 'one-workout-segments' ? workout : s.workoutB,
    }));
  }, []);

  const setWorkoutB = useCallback((workout: WorkoutSummary | null) => {
    setSession(s => ({ ...s, workoutB: workout }));
  }, []);

  const swapWorkouts = useCallback(() => {
    setSession(s => ({
      ...s,
      workoutA: s.workoutB,
      workoutB: s.workoutA,
      segmentA: s.segmentB,
      segmentB: s.segmentA,
    }));
  }, []);

  const setSegmentA = useCallback((segment: SegmentRange | null) => {
    setSession(s => ({ ...s, segmentA: segment }));
  }, []);

  const setSegmentB = useCallback((segment: SegmentRange | null) => {
    setSession(s => ({ ...s, segmentB: segment }));
  }, []);

  const reset = useCallback(() => {
    setSession(defaultSession());
    setCompareTick(0);
  }, []);

  const loadSession = useCallback((next: ComparisonSession) => {
    setSession(next);
    setCompareTick(t => t + 1);
  }, []);

  const triggerCompare = useCallback(() => {
    setCompareTick(t => t + 1);
  }, []);

  const applyPrefill = useCallback((prefill: ComparisonPrefill) => {
    setSession(s => ({
      ...sessionFromPrefill(prefill),
      workoutA: s.workoutA,
      workoutB: s.workoutB,
    }));
  }, []);

  return {
    session,
    compareTick,
    setMode,
    setViewMode,
    setMetric,
    setAlignment,
    setWorkoutA,
    setWorkoutB,
    swapWorkouts,
    setSegmentA,
    setSegmentB,
    reset,
    loadSession,
    triggerCompare,
    applyPrefill,
  };
}

export type ComparisonSessionApi = ReturnType<typeof useComparisonSession>;
